import React from "react";
import { Car, Home, Droplets, Wind, SprayCan, Shield } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const detailingItems = [
  { icon: SprayCan, label: "Interior Detail" },
  { icon: Shield, label: "Ceramic Coatings" },
  { icon: Droplets, label: "Foam Wash & Wax" },
  { icon: Wind, label: "Paint Correction" },
];

const pressureItems = [
  "House Washing",
  "Driveway Cleaning",
  "Sidewalk Cleaning",
  "Patio Cleaning",
  "Fence Cleaning",
  "Deck Cleaning",
  "Gutter Brightening",
];

export default function ServicesBento() {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-400" />
              <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
                What We Do
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-none text-white max-w-3xl">
              Two crafts. <span className="text-cyan-400">One obsession.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            From showroom-grade auto detailing to high-pressure exterior
            restoration for your home &mdash; we bring the same relentless attention
            to every job.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Auto Detailing – big card */}
          <article
            data-testid={SERVICES.detailingCard}
            className="md:col-span-8 relative overflow-hidden border border-white/10 bg-white/[0.02] backdrop-blur-md min-h-[420px] group"
          >
            <img
              src="https://images.unsplash.com/photo-1608506375591-b90e1f955e4b?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Auto detailing"
              className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-12">
              <div className="flex items-center gap-3 text-cyan-400 uppercase tracking-widest text-xs">
                <Car className="w-4 h-4" /> Auto Detailing
              </div>
              <div>
                <h3 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none">
                  Auto Detailing
                  <br />
                  <span className="text-zinc-500">&amp; Ceramic Protection</span>
                </h3>
                <p className="mt-4 text-zinc-300 max-w-lg leading-relaxed">
                  Full interior and exterior detailing, clay bar decontamination,
                  paint correction and ceramic coatings up to 3 layers.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {detailingItems.map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-3 border border-white/10 bg-black/40 px-4 py-3 text-sm text-zinc-200"
                    >
                      <Icon className="w-4 h-4 text-cyan-400" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Pressure Washing */}
          <article
            data-testid={SERVICES.pressureCard}
            className="md:col-span-4 relative overflow-hidden border border-white/10 bg-white/[0.02] backdrop-blur-md min-h-[420px] group"
          >
            <img
              src="https://images.unsplash.com/photo-1711098256664-d2ea62e6e376?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Pressure washing"
              className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
            <div className="relative z-10 h-full flex flex-col p-8 md:p-10">
              <div className="flex items-center gap-3 text-cyan-400 uppercase tracking-widest text-xs">
                <Home className="w-4 h-4" /> Residential
              </div>
              <h3 className="mt-6 font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter text-white leading-none">
                Pressure
                <br />
                Washing
              </h3>
              <ul className="mt-6 space-y-3 flex-1">
                {pressureItems.map((s) => (
                  <li
                    key={s}
                    className="flex items-center gap-3 text-sm text-zinc-200"
                  >
                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
